import { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { CreditCard, CheckCircle, XCircle, Clock, Search, Filter, Calendar, DollarSign, User, Package } from 'lucide-react';

interface OmiseCharge {
  id: string;
  omise_charge_id: string;
  userId: string;
  amount: number;
  status: 'pending' | 'successful' | 'failed';
  description: string;
  created_at: Date;
  paid_at?: Date;
  userEmail?: string;
}

interface OmiseSubscription {
  id: string;
  userId: string;
  plan_id: string;
  current_plan: string;
  billing_cycle: string;
  status: string;
  amount: number;
  period_start: Date;
  period_end: Date;
  userEmail?: string;
}

export default function OmisePayments() {
  const [charges, setCharges] = useState<OmiseCharge[]>([]);
  const [subscriptions, setSubscriptions] = useState<OmiseSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'charges' | 'subscriptions'>('charges');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const chargesQuery = query(
        collection(db, 'omise_charges'),
        orderBy('created_at', 'desc'),
        limit(100)
      );

      const chargesSnap = await getDocs(chargesQuery);
      const chargesData = await Promise.all(
        chargesSnap.docs.map(async (doc) => {
          const data = doc.data();
          const userDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', data.userId)));
          const userEmail = userDoc.docs[0]?.data()?.email || 'Unknown';

          return {
            id: doc.id,
            omise_charge_id: data.omise_charge_id,
            userId: data.userId,
            amount: data.amount,
            status: data.status,
            description: data.description,
            created_at: data.created_at.toDate(),
            paid_at: data.paid_at?.toDate(),
            userEmail,
          };
        })
      );
      setCharges(chargesData);

      const subscriptionsQuery = query(
        collection(db, 'omise_subscriptions'),
        orderBy('created_at', 'desc'),
        limit(100)
      );

      const subscriptionsSnap = await getDocs(subscriptionsQuery);
      const subscriptionsData = await Promise.all(
        subscriptionsSnap.docs.map(async (doc) => {
          const data = doc.data();
          const userDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', data.userId)));
          const userEmail = userDoc.docs[0]?.data()?.email || 'Unknown';

          return {
            id: doc.id,
            userId: data.userId,
            plan_id: data.plan_id,
            current_plan: data.current_plan,
            billing_cycle: data.billing_cycle,
            status: data.status,
            amount: data.amount,
            period_start: data.period_start.toDate(),
            period_end: data.period_end.toDate(),
            userEmail,
          };
        })
      );
      setSubscriptions(subscriptionsData);
    } catch (error) {
      console.error('Error loading Omise data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { icon: any; text: string; className: string }> = {
      successful: {
        icon: CheckCircle,
        text: 'สำเร็จ',
        className: 'bg-green-100 text-green-800',
      },
      pending: {
        icon: Clock,
        text: 'รอดำเนินการ',
        className: 'bg-yellow-100 text-yellow-800',
      },
      failed: {
        icon: XCircle,
        text: 'ล้มเหลว',
        className: 'bg-red-100 text-red-800',
      },
      active: {
        icon: CheckCircle,
        text: 'ใช้งานอยู่',
        className: 'bg-green-100 text-green-800',
      },
      cancelled: {
        icon: XCircle,
        text: 'ยกเลิก',
        className: 'bg-gray-100 text-gray-800',
      },
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${badge.className}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const filteredCharges = charges.filter((charge) => {
    const matchesSearch = 
      charge.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      charge.omise_charge_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      charge.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || charge.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const filteredSubscriptions = subscriptions.filter((sub) => {
    const matchesSearch = 
      sub.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.plan_id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const totalRevenue = charges
    .filter((c) => c.status === 'successful')
    .reduce((sum, c) => sum + c.amount, 0);

  const successfulCharges = charges.filter((c) => c.status === 'successful').length;
  const activeSubscriptions = subscriptions.filter((s) => s.status === 'active').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Omise Payments</h1>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">รายได้ทั้งหมด</span>
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">฿{totalRevenue.toLocaleString()}</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">การชำระเงินสำเร็จ</span>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{successfulCharges}</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">Subscriptions ที่ใช้งาน</span>
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{activeSubscriptions}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="ค้นหาด้วย email, charge ID, หรือคำอธิบาย..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none"
              >
                <option value="all">ทั้งหมด</option>
                <option value="successful">สำเร็จ</option>
                <option value="pending">รอดำเนินการ</option>
                <option value="failed">ล้มเหลว</option>
                <option value="active">ใช้งานอยู่</option>
                <option value="cancelled">ยกเลิก</option>
              </select>
            </div>
          </div>

          <div className="flex gap-4 mb-6 border-b">
            <button
              onClick={() => setActiveTab('charges')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'charges'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Charges ({filteredCharges.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'subscriptions'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Subscriptions ({filteredSubscriptions.length})
              </div>
            </button>
          </div>

          {activeTab === 'charges' ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">User</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Charge ID</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">คำอธิบาย</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">จำนวนเงิน</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">สถานะ</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">วันที่</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCharges.map((charge) => (
                    <tr key={charge.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="text-sm">{charge.userEmail}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {charge.omise_charge_id.substring(0, 20)}...
                        </code>
                      </td>
                      <td className="py-3 px-4 text-sm">{charge.description}</td>
                      <td className="py-3 px-4 text-right font-semibold">
                        ฿{charge.amount.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {getStatusBadge(charge.status)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          {charge.created_at.toLocaleDateString('th-TH')}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">User</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">แพ็กเกจ</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">รอบบิล</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">จำนวนเงิน</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">สถานะ</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">หมดอายุ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubscriptions.map((sub) => (
                    <tr key={sub.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="text-sm">{sub.userEmail}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-semibold">{sub.current_plan}</span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {sub.billing_cycle === 'yearly' ? 'รายปี' : 'รายเดือน'}
                      </td>
                      <td className="py-3 px-4 text-right font-semibold">
                        ฿{sub.amount.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {getStatusBadge(sub.status)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          {sub.period_end.toLocaleDateString('th-TH')}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
